import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject, ReplaySubject, of } from 'rxjs';
import { UserRecord, GetFamilyLinksItem, Link } from 'src/app/user/user.model';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { tap, switchMap, map, takeUntil, catchError } from 'rxjs/operators';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { userRecordSelector } from 'src/app/user/store/user.selectors';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { languageSelector } from 'src/app/global/store/global.selectors';
import { AddFamilyMemberDialogComponent } from 'src/app/user/dialogs/add-family-member-dialog/add-family-member-dialog.component';
import { EditFamilyLinkDialogComponent } from 'src/app/user/dialogs/edit-family-link-dialog/edit-family-link-dialog.component';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { RemoveFamilyConfirmDialogComponent } from 'src/app/user/dialogs/remove-family-confirm-dialog/remove-family-confirm-dialog.component';
import { ForceAcceptFamilyLinkDialogComponent } from 'src/app/user/dialogs/force-accept-family-link-dialog/force-accept-family-link-dialog.component';

@Component({
    selector: 'app-user-family',
    templateUrl: './user-family.component.html',
    styleUrls: ['./user-family.component.scss'],
})
export class UserFamilyComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();
    update$ = new ReplaySubject();

    public loading = false;
    public activeLinks: GetFamilyLinksItem[] = [];
    public pendingApprovalLinks: GetFamilyLinksItem[] = [];
    public pendingInvitationLinks: GetFamilyLinksItem[] = [];
    public userRecord: UserRecord;

    constructor(
        private dialog: MatDialog,
        private userService: UserService,
        private route: ActivatedRoute,
        private translateService: TranslateService,
        private messageService: MessageService,
        private store: Store<State>,
    ) {}

    ngOnInit(): void {
        this.update$
            .pipe(
                tap(() => (this.loading = true)),
                switchMap(() => this.route.paramMap),
                map((params) => params.get('id')),
                switchMap((id) =>
                    this.userService.getUserFamilyLinks(id).pipe(
                        takeUntil(this.destroy$),
                        catchError((error) => {
                            this.messageService.error(error.messages?.[0] || this.translateService.instant('pages.userFamily.error.common'));
                            return of([]);
                        }),
                        tap((links) => (this.activeLinks = links.filter((l) => l.cityApproved && l.familyApproved))),
                        tap((links) => (this.pendingApprovalLinks = links.filter((l) => !l.cityApproved && l.familyApproved))),
                        tap((links) => (this.pendingInvitationLinks = links.filter((l) => (!l.cityApproved && !l.familyApproved) || (l.cityApproved && !l.familyApproved)))),
                        tap(() => (this.loading = false)),
                    ),
                ),
            )
            .subscribe();

        this.store
            .select(userRecordSelector)
            .pipe(
                takeUntil(this.destroy$),
                tap((userRecord) => (this.userRecord = userRecord)),
            )
            .subscribe();

        this.store
            .select(languageSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((lang) => this.translateService.use(lang));

        this.update$.next();
    }

    openCreateCardDialog(): void {
        this.dialog
            .open(AddFamilyMemberDialogComponent, {
                panelClass: 'create-dialog',
                data: { userRecord: this.userRecord },
            })
            .afterClosed()
            .subscribe((res) => {
                if (res) {
                    this.update$.next();
                }
            });
    }

    openEditFamilyLinkDialog(link: Link): void {
        this.dialog
            .open(EditFamilyLinkDialogComponent, {
                panelClass: 'edit-dialog',
                autoFocus: false,
                data: {
                    userRecord: this.userRecord,
                    link,
                },
            })
            .afterClosed()
            .subscribe((res) => {
                if (res) {
                    this.update$.next();
                }
            });
    }

    openRemoveFamilyConfirmDialog(link: Link): void {
        this.dialog
            .open(RemoveFamilyConfirmDialogComponent, {
                panelClass: 'delete-dialog',
                autoFocus: false,
                data: {
                    userRecord: this.userRecord,
                    link,
                },
            })
            .afterClosed()
            .subscribe((res) => {
                if (res) {
                    this.update$.next();
                }
            });
    }

    openForceAcceptFamilyLinkDialog(link: Link): void {
        this.dialog
            .open(ForceAcceptFamilyLinkDialogComponent, {
                panelClass: 'force-accept-dialog',
                autoFocus: false,
                data: {
                    userRecord: this.userRecord,
                    link,
                },
            })
            .afterClosed()
            .subscribe((res) => {
                if (res) {
                    this.update$.next();
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
